import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useState } from 'react';
import { useTheme } from 'styled-components';

import Modal from '../../../../../../components/Modal';
import SelectAsyncJSX from '../../../../../../components/SelectAsync';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import HttpStatus from '../../../../../../enums/httpStatus';
import { reconcileRoutinesAPI, sellersApi } from '../../../../../../services/requests';
import { getOptionsFromSellers, SelectOption } from '../../../../../../utils/getSelectOptions';

type ReconciliationResult = {
  skipped: boolean;
  reason?: string;
  removed_routines: number;
  removed_scheduled_visits: number;
  sellers_processed: number;
  sellers_with_divergences?: number;
};

type Props = {
  closeModal: () => void;
  callback: () => void;
};

const ModalReconcileRoutines = ({ closeModal, callback }: Props) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [seller, setSeller] = useState<SelectOption | null>(null);
  const [result, setResult] = useState<ReconciliationResult | null>(null);

  const handleReconcile = async () => {
    setIsLoading(true);
    setResult(null);

    try {
      const payload = seller ? { CodigoVendedor: seller.value } : {};
      const res = await reconcileRoutinesAPI.post(payload);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const reconciliationResult: ReconciliationResult = res.data.data;
      setResult(reconciliationResult);

      if (reconciliationResult.skipped) {
        frontendNotification({
          message: t('visitReconcileSkipped'),
          type: 'warning',
        });
      } else {
        frontendNotification({
          message: t('visitReconcileSuccess'),
          type: 'success',
        });
        callback();
      }
    } catch (err) {
      if (!reconcileRoutinesAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
  };

  const description = seller
    ? t('visitReconcileSellerDescription', {
      sellerCode: seller.value,
      sellerName: seller.label,
    })
    : t('visitReconcileAllDescription');

  return (
    <Modal
      title={t('visitReconcileTitle')}
      action={handleReconcile}
      showModal
      closeModal={closeModal}
      cancelButton={closeModal}
      actionNameBtn={t('confirm')}
      isLoading={isLoading}
      style={{ width: 'min(80vw, 520px)' }}
      bodyStyle={{ paddingLeft: '1rem', paddingRight: '1rem' }}
    >
      <div style={{ padding: '1rem', display: 'grid', gap: '1rem' }}>
        <div>{description as string}</div>

        <div className="itemFormWithLabel">
          <div className="label">{t('seller') as string} ({t('optional') as string})</div>
          <SelectAsyncJSX
            value={seller}
            onChange={(e: any) => setSeller(e)}
            request={sellersApi}
            reqResponseToOption={getOptionsFromSellers}
            placeholder={t('allSellers') as string}
            disabled={isLoading}
          />
        </div>

        <small style={{ color: theme.colors.warning, fontWeight: 600 }}>
          {t('visitReconcileWarning') as string}
        </small>

        {result && !result.skipped && (
          <div
            style={{
              background: theme.colors.surface,
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              display: 'grid',
              gap: '0.25rem',
              fontSize: '0.875rem',
            }}
          >
            <strong>{t('visitReconcileResultTitle') as string}</strong>
            <span>{t('visitReconcileResultSellers', { count: result.sellers_processed }) as string}</span>
            <span>
              {t('visitReconcileResultSellersWithDivergences', { count: result.sellers_with_divergences ?? 0 }) as string}
            </span>
            <span style={{ color: result.removed_routines > 0 ? theme.colors.warning : undefined }}>
              {t('visitReconcileResultRoutines', { count: result.removed_routines }) as string}
            </span>
            <span style={{ color: result.removed_scheduled_visits > 0 ? theme.colors.warning : undefined }}>
              {t('visitReconcileResultVisits', { count: result.removed_scheduled_visits }) as string}
            </span>
          </div>
        )}

        {result?.skipped && (
          <small style={{ color: theme.colors.negative }}>
            {result.reason}
          </small>
        )}
      </div>
    </Modal>
  );
};

export default ModalReconcileRoutines;
